import React, {useState, useEffect} from 'react'
import {useLocation} from 'react-router-dom'
import useSiteCategory from '../../hooks/use-site-category'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// Chakra
import {Box, Fade, useDisclosure} from '@salesforce/retail-react-app/app/components/shared/ui'

// Local Project Components
import Header from '../header'
import {DrawerMenu} from '../drawer-menu'
import {ListMenu, ListMenuContent} from '../list-menu'
import {NestedAccordionPanelSkeleton} from '../nested-accordion'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

// HOCs
import {withCommerceSdkReact} from '@salesforce/retail-react-app/app/components/with-commerce-sdk-react/with-commerce-sdk-react'

// Others
import {
    YR_HOME_HREF,
    KS_HOME_HREF,
    CAT_MENU_DEFAULT_NAV_SSR_DEPTH,
    KS_MY_ACCOUNT_URL
} from '../../constants'
import LoadingSpinner from '../loading-spinner'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {scheduleAfterPaint} from '../../utils/common-utils'

const DrawerMenuItemWithData = withCommerceSdkReact(
    ({itemComponent: ItemComponent, data, ...rest}) => (
        <Fade in={true}>
            <ItemComponent {...rest} item={data} itemComponent={DrawerMenuItemWithData} />
        </Fade>
    ),
    {
        hook: useSiteCategory,
        queryOptions: ({item}) => ({
            parameters: {
                id: item.id
            }
        }),
        placeholder: NestedAccordionPanelSkeleton
    }
)

const ListMenuContentWithData = withCommerceSdkReact(
    ({data, ...rest}) => (
        <Fade in={true}>
            <ListMenuContent {...rest} item={data} />
        </Fade>
    ),
    {
        hook: useSiteCategory,
        queryOptions: ({item}) => ({
            parameters: {
                id: item.id,
                levels: 2
            }
        }),
        placeholder: () => (
            <Box minHeight="350px" alignItems="center">
                <LoadingSpinner height="100%" background="gray.300" />
            </Box>
        )
    }
)

const HeaderWrapper = () => {
    const [showSpinner, setShowSpinner] = useState(false)
    const {isOpen, onOpen, onClose} = useDisclosure()
    const {buildUrl} = useMultiSite()
    const navigate = useNavigation()
    const location = useLocation()
    const isYR = location?.pathname?.includes('/yellow-rose')
    const {
        app: {commerceAPI}
    } = getConfig()
    const {hostUrl, rootCategoryId} = commerceAPI.storefront

    const {data: categoriesTree} = useSiteCategory({
        parameters: {id: isYR ? rootCategoryId : 'root', levels: CAT_MENU_DEFAULT_NAV_SSR_DEPTH}
    })

    useEffect(() => {
        // Note: Please remove this once we move KS to PWA
        const resetStateOnBack = (event) => {
            if (event.persisted) {
                // Page was restored from bfcache (back-forward cache)
                setShowSpinner(false)
            }
        }
        window.addEventListener('pageshow', resetStateOnBack)
        return () => {
            window.removeEventListener('pageshow', resetStateOnBack)
        }
    }, [])

    useEffect(() => {
        // Lets automatically close the mobile navigation when the
        // location path is changed.
        onClose()
    }, [location])

    const onLogoClick = () => {
        // Keep close work in the immediate interaction so the drawer responds instantly.
        onClose?.()

        const path = buildUrl(isYR ? YR_HOME_HREF : KS_HOME_HREF)

        // Avoid redundant route work when already on the target page.
        if (location?.pathname === path) {
            return
        }

        // Defer routing until after paint so close animation/feedback wins the INP path.
        scheduleAfterPaint(() => {
            navigate(path)
        })
    }

    const onAccountClick = () => {
        window.location.href = hostUrl + KS_MY_ACCOUNT_URL
    }

    const onWishlistClick = () => {
        // Link to wishlist page if registered; Header component will show auth modal for guest users
        const path = buildUrl('/account/wishlist')
        history.push(path)
    }

    return (
        <Header
            onMenuClick={onOpen}
            onLogoClick={onLogoClick}
            onMyAccountClick={onAccountClick}
            onWishlistClick={onWishlistClick}
            className="header-wrapper"
        >
            <HideOnDesktop className="header-wrapper-mobile">
                {/* This Spinner will be shown for the Phase 1 only if user is going to SFRA URLs via search. */}
                {showSpinner && <LoadingSpinner />}
                <DrawerMenu
                    isOpen={isOpen}
                    onClose={onClose}
                    root={categoriesTree}
                    setShowSpinner={(bool) => {
                        setShowSpinner(bool)
                        onClose()
                    }}
                    itemsKey="categories"
                    itemsCountKey="onlineSubCategoriesCount"
                    itemComponent={DrawerMenuItemWithData}
                />
            </HideOnDesktop>

            <HideOnMobile className="desktop-list-menu">
                <ListMenu
                    root={categoriesTree}
                    itemsKey="categories"
                    itemsCountKey="onlineSubCategoriesCount"
                    margin="auto"
                    contentComponent={ListMenuContentWithData}
                />
            </HideOnMobile>
        </Header>
    )
}

export default HeaderWrapper
